/**
 * Puppet maths for the player (docs/SKEL-STORY.md §3.5–§3.6) — pure, no pixi, no DOM.
 *
 * Every actor is a SkelForm puppet: `cast[h].puppet = {default_view, views, actions}`. A cue's
 * `clip` is a **story action** (`idle`, `move`, `talk`, `wave`, …); this module turns
 * `(action, story time)` into one Pixi-ready transform per textured bone, with the same maths the
 * editor draws with (`src/skel/{animate,construct,draw}`), so a puppet on stage is pixel-for-pixel
 * the puppet in the editor up to texture filtering.
 *
 * 1. **Resolution** (`resolveAction`): action → `(view, animation)`. The baker lists the ways to
 *    play an action in preference order; the player takes the first whose view exists, preferring
 *    a side-like view (facing left/right) for locomotion and the puppet's `default_view` for
 *    everything else. A missing action falls back through a short alias list, then `idle`, then
 *    any action — and a puppet with no animations at all still stands in its rest pose.
 * 2. **Time**: the pose is a pure function of the runner clock (`clipTimeMs`), so `seek(t)` draws
 *    exactly what a play-through draws. The 280 ms crossfade (Q9) is also pure: the snapshot carries
 *    the previous clip and its start (`prevClip`, `prevClipTimeMs`, `sinceChangeMs`). Within one
 *    view the two poses blend (`blendPoses`, shortest-arc); across views (front ↔ side) the body
 *    turns through edge-on — the old view narrows away, then the new one widens in — so there is
 *    only ever one body on screen. During a hold the clock is frozen, so a looping
 *    animation adds the presentation-only `holdWallMs` — the puppet keeps breathing at "The end".
 *
 *    Every frame poses every actor, so what does not change between frames is cached per object:
 *    the action → view binding per puppet, and each animation's keyframe channels (bundle data is
 *    never edited while it plays).
 * 3. **Transform** (`puppetSpritesAt`): world y-up → Pixi y-down inside a view container whose
 *    origin is the feet: `position (cx·k, −(cy − y0)·k)`, `rotation −rot`, `scale (sx·k, sy·k)`
 *    in texture-size units (the view multiplies by `tex.size / texture.width`, so a baker may ship
 *    half-resolution parts). `quadOf` already carries SkelForm's `pivot_rot · dir` mirror rule.
 *    `k` = on-stage body height / the view's `height_px`, so every view is the same height on
 *    screen; `y0` = the view's rest-pose floor (`bounds.y0`). Facing is a flip of the **view**
 *    container (`scale.x = −1`), decided by the view's authored facing: `camera` views never flip.
 */
import { blendPoses, channelsOf, frameAtTime, samplePose } from '@/skel/animate'
import type { Pose } from '@/skel/animate'
import { construct } from '@/skel/construct'
import type { WorldBone } from '@/skel/construct'
import { drawOrder, drawStylesOf, quadOf, resolveTexture } from '@/skel/draw'
import { maskBoneIds, overrideBones } from '@/skel/layer'
import { socketPoint } from '@/skel/socket'
import type { SocketDef } from '@/skel/socket'
import { texKey } from '@/skel/types'
import type { SkfAnimation, SkfKeyframe, SkfRoot, Tint } from '@/skel/types'
import type { BundlePuppet, BundlePuppetView, PuppetActionRef, PuppetSocket } from '@/schema/story'

/** Q9(a): the crossfade between two actions of one view, smoothstep-weighted. */
export const CROSSFADE_MS = 280
/** Q9(d): a view switch (front ↔ side) turns through edge-on over this long (half out, half in). */
export const VIEW_FADE_MS = 220
/** The narrowest a view gets at the edge-on point of a view switch (never a zero scale). */
export const VIEW_EDGE_MIN = 0.02
/** Q9(c): a facing change turns the flippable views over this long. */
export const TURN_MS = 180
/** Q9(c): how much a turning view squashes vertically at the middle of the turn. */
export const TURN_SQUASH = 0.06
/** Q6: an upper-body layer blends in and out over this long. */
export const LAYER_BLEND_MS = 200

export const smoothstep = (u: number): number => {
  const x = u <= 0 ? 0 : u >= 1 ? 1 : u
  return x * x * (3 - 2 * x)
}

/* ---------------------------------------------------------------- facing */

/** The slice of a view (or any authored thing) the facing rule reads. */
export interface FacingRef {
  facing?: string | null
  /** The ref is itself a mirrored take of its art (XORed in). */
  mirror?: boolean | null
}

/**
 * Which way something looks as authored: `+1` right, `−1` left, `0` for a view that must never be
 * flipped by facing (`camera`, `away`, anything unknown). A three-quarter view counts as its side.
 */
export function authoredSide(facing: string | null | undefined): 1 | -1 | 0 {
  switch (facing) {
    case 'right':
    case '3q_right':
      return 1
    case 'left':
    case '3q_left':
      return -1
    default:
      return 0
  }
}

/**
 * Should the art be drawn flipped so the character looks `desired` on screen? Flip when the
 * desired side differs from the authored side, XOR the ref's own `mirror`. A `camera` view is
 * never flipped by facing — a face turned to the child stays turned to the child.
 */
export function mirrorFor(desired: 1 | -1, ref: FacingRef | null | undefined): boolean {
  const own = ref?.mirror === true
  const side = authoredSide(ref?.facing)
  if (side === 0) return own
  return (side !== desired) !== own
}

/* ------------------------------------------------------------ resolution */

/** Locomotion actions: they prefer a side-like view (the one that can walk across the stage). */
export const LOCOMOTION_ACTIONS: ReadonlySet<string> = new Set([
  'move', 'walk', 'run', 'fly', 'swim', 'hop', 'slither', 'crawl', 'bounce', 'march', 'gallop',
])

/** What to try, after the action itself, before falling back to `idle`. */
const ALIASES: Readonly<Record<string, readonly string[]>> = {
  move: ['walk', 'run', 'fly', 'swim', 'slither', 'hop', 'bounce', 'march'],
  walk: ['move', 'march', 'run'],
  run: ['move', 'walk'],
  fly: ['move', 'hop'],
  swim: ['move'],
  slither: ['move'],
  crawl: ['move', 'walk'],
  hop: ['move', 'jump'],
  march: ['move', 'walk'],
  gallop: ['move', 'run'],
  greet: ['wave'],
  wave: ['greet'],
  happy: ['cheer', 'dance', 'jump'],
  cheer: ['happy', 'jump'],
  surprised: ['jump'],
  // Part 2 interaction ladders (Q5), for puppets rigged before the presets existed.
  catch: ['reach', 'jump'],
  throw: ['wave', 'greet'],
  kick: ['jump'],
  eat: ['talk'],
  peck: ['eat', 'talk'],
  ride: ['sit'],
  hide: ['sit'],
  sleep: ['sit'],
  push: ['move', 'walk'],
  point: ['wave', 'greet'],
  hug: ['happy', 'greet'],
  pick_up: ['reach', 'wave'],
  put_down: ['reach', 'wave'],
  reach: ['wave', 'greet'],
}

export function isLocomotion(action: string): boolean {
  return LOCOMOTION_ACTIONS.has(action)
}

/** A resolved action: which view and which animation of it to play. */
export interface PuppetBinding {
  /** The action key that matched in `puppet.actions` (may differ from the one asked for). */
  action: string
  view: string
  /** Index into the view root's `animations`; −1 = the rest pose. */
  anim: number
  loop: boolean
  ref: PuppetActionRef | null
}

/** The view's root, typed for the skel runtime (the schema checks it only structurally). */
export function rootOf(view: BundlePuppetView): SkfRoot {
  return view.root as unknown as SkfRoot
}

/** Animation names compare loosely: `"Kick ball"`, `kick_ball` and `kick-ball` are one name. */
function nameKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/** The animation a ref names: its `index` when that animation carries its name, else by name. */
export function animIndexOf(root: SkfRoot, ref: Pick<PuppetActionRef, 'anim' | 'index'>): number {
  const anims = root.animations ?? []
  const want = nameKey(ref.anim ?? '')
  const at = anims[ref.index]
  if (at && (want === '' || nameKey(at.name) === want)) return ref.index
  if (want !== '') {
    const byName = anims.findIndex((a) => nameKey(a.name) === want)
    if (byName >= 0) return byName
  }
  return at ? ref.index : -1
}

function pickRef(puppet: BundlePuppet, asked: string, key: string, refs: readonly PuppetActionRef[]): PuppetActionRef | undefined {
  if (isLocomotion(asked) || isLocomotion(key)) {
    return refs.find((r) => authoredSide(puppet.views[r.view]?.facing) !== 0) ?? refs[0]
  }
  return refs.find((r) => r.view === puppet.default_view) ?? refs[0]
}

interface BindingCache {
  actions: unknown
  views: unknown
  keys: number
  byAction: Map<string, PuppetBinding | null>
}
/** Per puppet: the binding of every action asked so far. Invalidated when its tables change. */
const bindingCache = new WeakMap<BundlePuppet, BindingCache>()

/**
 * `(action)` → `(view, animation)`, or `null` for a puppet with no views. Deterministic: the same
 * puppet and action always bind the same way, so the runner (flip) and the view (pixels) agree.
 *
 * Called several times per actor per frame (the pose, the mirror rule, the body height), so the
 * answer is cached per puppet object; a puppet whose `actions` / `views` table is replaced, or
 * gains an action, is resolved afresh. The returned binding is shared: treat it as read-only.
 */
export function resolveAction(puppet: BundlePuppet | null | undefined, asked: string): PuppetBinding | null {
  if (!puppet || !puppet.views) return null
  const keys = puppet.actions ? Object.keys(puppet.actions).length : 0
  let entry = bindingCache.get(puppet)
  if (!entry || entry.actions !== puppet.actions || entry.views !== puppet.views || entry.keys !== keys) {
    entry = { actions: puppet.actions, views: puppet.views, keys, byAction: new Map() }
    bindingCache.set(puppet, entry)
  }
  const hit = entry.byAction.get(asked)
  if (hit !== undefined) return hit
  const b = resolveActionUncached(puppet, asked)
  entry.byAction.set(asked, b)
  return b
}

function resolveActionUncached(puppet: BundlePuppet, asked: string): PuppetBinding | null {
  const actions = puppet.actions ?? {}
  const chain = [asked, ...(ALIASES[asked] ?? []), 'idle', ...Object.keys(actions)]
  for (const key of chain) {
    const refs = (actions[key] ?? []).filter((r) => puppet.views[r.view] !== undefined)
    if (refs.length === 0) continue
    const ref = pickRef(puppet, asked, key, refs)
    if (!ref) continue
    const view = puppet.views[ref.view] as BundlePuppetView
    return { action: key, view: ref.view, anim: animIndexOf(rootOf(view), ref), loop: ref.loop, ref }
  }
  // No playable action at all: stand still in the default (or first) view's rest pose.
  const view = puppet.views[puppet.default_view] ? puppet.default_view : Object.keys(puppet.views)[0]
  if (view === undefined) return null
  return { action: asked, view, anim: -1, loop: true, ref: null }
}

/** The authored facing of the view `action` binds to (`null` when the puppet has none). */
export function viewFacingFor(puppet: BundlePuppet | null | undefined, action: string): string | null {
  const b = resolveAction(puppet, action)
  return b ? (puppet?.views[b.view]?.facing ?? null) : null
}

/* ------------------------------------------------------------------ time */

/** The clock half of an `ActorFrame`: everything the pose depends on. */
export interface PuppetClock {
  /** Story action bound now. */
  clip: string
  /** Milliseconds into it — story clock, audio-mastered during lines. */
  clipTimeMs: number
  /** The action before it (for the crossfade), or null. */
  prevClip: string | null
  /** Milliseconds into the previous action at this same instant. */
  prevClipTimeMs: number
  /** Milliseconds since the change to `clip`. */
  sinceChangeMs: number
  /** Wall ms spent at the current hold (presentation only), or null/absent when not holding. */
  holdWallMs?: number | null
  /**
   * Q9(a): the actor's own idle clock (ms) — never restarts, so idle → act → idle resumes the
   * breathing where it would have been instead of popping to frame 0. Used whenever an action
   * resolves to `idle`; absent = the clip's own time.
   */
  idleClockMs?: number | null
  /** Q6: a masked upper-body layer over the base clip, or null. */
  layer?: PuppetLayerInput | null
}

/** Q6: what the upper-body layer plays and how strongly (0..1, already eased). */
export interface PuppetLayerInput {
  clip: string
  /** `puppet.masks` name (`upper_near`, `upper_r`, …); resolved per view with a name heuristic. */
  mask: string
  timeMs: number
  weight: number
}

/** 0 → still entirely the previous action, 1 → entirely the new one. Smoothstep in time (Q9a). */
export function crossfadeWeight(sinceChangeMs: number, ms: number = CROSSFADE_MS): number {
  if (!Number.isFinite(sinceChangeMs) || ms <= 0) return 1
  return smoothstep(sinceChangeMs / ms)
}

/**
 * Q9(c): the x-factor of a flippable view `sinceMs` after the actor turned: −1 (still the old
 * look) → 0 (edge-on) → 1 (the new look), eased, over `TURN_MS`.
 */
export function turnFactor(sinceMs: number, ms: number = TURN_MS): number {
  if (!Number.isFinite(sinceMs) || sinceMs >= ms || ms <= 0) return 1
  if (sinceMs <= 0) return -1
  return -Math.cos(Math.PI * smoothstep(sinceMs / ms))
}

interface ChannelCache {
  keyframes: SkfKeyframe[]
  n: number
  channels: Map<string, SkfKeyframe[]>
}
/** Per animation: its keyframes grouped and sorted per channel (`channelsOf` sorts on every call). */
const channelCache = new WeakMap<SkfAnimation, ChannelCache>()

function channelsFor(anim: SkfAnimation): Map<string, SkfKeyframe[]> {
  const hit = channelCache.get(anim)
  if (hit && hit.keyframes === anim.keyframes && hit.n === anim.keyframes.length) return hit.channels
  const channels = channelsOf(anim)
  channelCache.set(anim, { keyframes: anim.keyframes, n: anim.keyframes.length, channels })
  return channels
}

function sampleAt(root: SkfRoot, b: PuppetBinding, timeMs: number): { pose: Pose; frame: number } {
  if (b.anim < 0) return { pose: samplePose(root, null, 0), frame: 0 }
  const anim = root.animations?.[b.anim]
  const frame = frameAtTime(anim, Math.max(0, timeMs) / 1000, b.loop)
  if (!anim || !Array.isArray(anim.keyframes)) return { pose: samplePose(root, b.anim, frame, { loop: b.loop }), frame }
  return { pose: samplePose(root, b.anim, frame, { loop: b.loop, channels: channelsFor(anim) }), frame }
}

/* ----------------------------------------------------------------- masks */

const ARM_BONE = /(arm|hand|wrist|elbow|shoulder|claw|wing)/i
const SIDE_OF_MASK = /_(near|far|r|l|left|right|front|back)$/i

/**
 * Bone names of mask `mask` for view `view`: the view's own `masks`, then `puppet.masks[view]`,
 * then a flat `puppet.masks[mask]`; failing all three, the arm chain on the mask's side by name
 * (`upper_near` → `*arm*_near`, `hand_near`, …; `upper` → every arm bone). Empty = no layer.
 */
export function maskBonesFor(puppet: BundlePuppet, view: string, mask: string): string[] {
  const v = puppet.views[view] as (BundlePuppetView & { masks?: Record<string, string[]> }) | undefined
  const own = v?.masks?.[mask]
  if (Array.isArray(own)) return own
  const table = puppet.masks as Record<string, string[] | Record<string, string[]>> | undefined
  const perView = table?.[view]
  if (perView && !Array.isArray(perView) && Array.isArray(perView[mask])) return perView[mask]
  const flat = table?.[mask]
  if (Array.isArray(flat)) return flat
  if (!v) return []
  const arms = rootOf(v).bones.map((b) => b.name).filter((n) => ARM_BONE.test(n))
  const side = SIDE_OF_MASK.exec(mask)?.[1]?.toLowerCase()
  if (!side) return arms
  const alt: Record<string, string[]> = { r: ['r', 'right', 'near'], right: ['right', 'r', 'near'], near: ['near', 'r', 'right'], l: ['l', 'left', 'far'], left: ['left', 'l', 'far'], far: ['far', 'l', 'left'], front: ['front', 'near'], back: ['back', 'far'] }
  for (const s of alt[side] ?? [side]) {
    const sided = arms.filter((n) => n.toLowerCase().endsWith(`_${s}`))
    if (sided.length > 0) return sided
  }
  return arms
}

/** The ref that plays `action` in `view` itself (a layer must not switch views), with aliases. */
function bindingInView(puppet: BundlePuppet, action: string, view: string): PuppetBinding | null {
  const v = puppet.views[view]
  if (!v) return null
  for (const key of [action, ...(ALIASES[action] ?? [])]) {
    const ref = (puppet.actions?.[key] ?? []).find((r) => r.view === view)
    if (ref) return { action: key, view, anim: animIndexOf(rootOf(v), ref), loop: ref.loop, ref }
  }
  return null
}

/** `pose` with the layer's mask bones taken from the layer clip (Q6). Unchanged when it can't apply. */
function withLayer(puppet: BundlePuppet, view: string, root: SkfRoot, pose: Pose, layer: PuppetLayerInput | null | undefined): Pose {
  if (!layer || !(layer.weight > 0)) return pose
  const b = bindingInView(puppet, layer.clip, view)
  if (!b || b.anim < 0) return pose
  const ids = maskBoneIds(root, maskBonesFor(puppet, view, layer.mask))
  if (ids.length === 0) return pose
  return overrideBones(root, pose, sampleAt(root, b, layer.timeMs).pose, ids, layer.weight)
}

/* ------------------------------------------------------------- transform */

/** Everything `puppetSpritesAt` needs: the clock, the facing, and `k` for the bound view. */
export interface PuppetFrameInput extends PuppetClock {
  facing: 1 | -1
  /** Stage px per world px **of the view `clip` binds to** (`heightPx / bodyHeight(clip)`). */
  scale: number
  /** Q9(c) `turnFactor` of the actor's last facing change (1 = settled). */
  turn?: number
}

/** One Pixi sprite, in its view container's space (origin = feet, y down, unflipped). */
export interface PuppetSpriteXf {
  bone: number
  name: string
  /** `"<styleId>/<texName>"`, the view's `textures` key. */
  texKey: string
  /** Bundle asset id of the part PNG, or null when the view does not ship it. */
  asset: string | null
  x: number
  y: number
  rotation: number
  /** Scale in texture-SIZE units: multiply by `texW / texture.width` for the real sprite scale. */
  scaleX: number
  scaleY: number
  texW: number
  texH: number
  /** 0xRRGGBB multiply. */
  tint: number
  alpha: number
  /** Rank in the draw order (stable ties by bone index). */
  zIndex: number
}

export interface PuppetViewXf {
  view: string
  /** Container alpha: 1 settled; during a view switch the new view fades in fast, the old one out. */
  alpha: number
  /** 0 bound view, 1 the previous view on top during a cross-view fade. */
  zIndex: number
  /** The view is drawn flipped for the actor's facing (its container's sign). */
  mirror: boolean
  /** Container scale: `±1 × turn` for a flippable view mid-turn (Q9c), `±1` otherwise. */
  scaleX: number
  /** Container scale y: `1 − squash` mid-turn, else 1. */
  scaleY: number
  /** Stage px per world px for this view. */
  k: number
  /** `bounds.y0`: the rest-pose floor, drawn on the feet line. */
  groundY: number
  anim: number
  frame: number
  sprites: PuppetSpriteXf[]
  /** World bones of the drawn pose (y up) — `socket()` reads them. */
  world: WorldBone[]
}

export interface PuppetXf {
  binding: PuppetBinding | null
  prev: PuppetBinding | null
  /** Crossfade weight of the bound action (1 = settled). */
  weight: number
  views: PuppetViewXf[]
}

const EMPTY_XF: PuppetXf = Object.freeze({ binding: null, prev: null, weight: 1, views: [] }) as unknown as PuppetXf

function to255(v: number): number {
  return Math.round(Math.min(Math.max(Number.isFinite(v) ? v : 1, 0), 1) * 255)
}

export function rgbOf(t: Tint | undefined): number {
  if (!t) return 0xffffff
  return (to255(t.r) << 16) | (to255(t.g) << 8) | to255(t.b)
}

function viewXf(
  name: string,
  view: BundlePuppetView,
  pose: Pose,
  k: number,
  facing: 1 | -1,
  turn: number,
  meta: { alpha: number; zIndex: number; anim: number; frame: number },
  /** Q9(d) view switch: horizontal width factor (1 = full, towards 0 = edge-on). */
  squeeze = 1,
): PuppetViewXf {
  const root = rootOf(view)
  const world = construct(root, pose)
  const groundY = Number.isFinite(view.bounds?.y0) ? view.bounds.y0 : 0
  // The editor's rule (`drawStylesOf`): an empty worn list draws every outfit, never none.
  const styles = drawStylesOf(view)
  const sprites: PuppetSpriteXf[] = []
  drawOrder(root, pose, world).forEach((id, rank) => {
    const def = root.bones[id]
    const bone = world[id]
    const visual = def ? root.visuals[def.visuals_id] : undefined
    if (!def || !bone || !visual) return
    const pv = pose.visuals[def.visuals_id]
    const res = resolveTexture(root, pv?.tex ?? visual.tex, styles)
    if (!res || !(res.tex.size.x > 0 && res.tex.size.y > 0)) return
    const key = texKey(res.styleId, res.tex.name)
    const q = quadOf(bone, visual, res.tex.size)
    const tint = pv?.tint ?? visual.tint
    sprites.push({
      bone: id,
      name: def.name,
      texKey: key,
      asset: view.textures[key] ?? null,
      x: q.cx * k,
      y: -(q.cy - groundY) * k,
      rotation: -q.rot,
      scaleX: q.sx * k,
      scaleY: q.sy * k,
      texW: res.tex.size.x,
      texH: res.tex.size.y,
      tint: rgbOf(tint),
      alpha: Math.min(1, Math.max(0, tint?.a ?? 1)),
      zIndex: rank,
    })
  })
  const mirror = mirrorFor(facing, view)
  // A camera view looks the same either way, so it never turns (and never squashes).
  const flippable = authoredSide(view.facing) !== 0
  const tf = flippable && Number.isFinite(turn) ? Math.max(-1, Math.min(1, turn)) : 1
  const sq = Number.isFinite(squeeze) ? Math.max(VIEW_EDGE_MIN, Math.min(1, squeeze)) : 1
  return {
    view: name,
    alpha: meta.alpha,
    zIndex: meta.zIndex,
    mirror,
    scaleX: (mirror ? -1 : 1) * tf * sq,
    scaleY: 1 - TURN_SQUASH * (1 - Math.abs(tf) * sq),
    k,
    groundY,
    anim: meta.anim,
    frame: meta.frame,
    sprites,
    world,
  }
}

/**
 * The whole puppet at one instant: per visible view, one transform per textured bone.
 * `entry` is a cast/prop entry carrying `puppet`, or the puppet itself.
 *
 * Q9: within one view the previous action blends into the new one over `CROSSFADE_MS` (smoothstep
 * weight); across views the body turns through edge-on over `VIEW_FADE_MS`, the way a facing turn
 * does: for the first half only the old view is drawn, narrowing to a sliver; for the second half
 * only the new one, widening from it. Exactly one opaque body at every instant — a dissolve drew
 * the old body over the new one for up to 220 ms (two boys, ~22 times a story), and `views[0]` is
 * always the view on screen, so held props and sockets follow what the child sees. A looping
 * action that resolves to `idle` reads the actor's `idleClockMs`, and a Q6 layer overrides the mask
 * bones of the bound view's pose last.
 */
export function puppetSpritesAt(
  entry: { puppet?: BundlePuppet | null } | BundlePuppet | null | undefined,
  frame: PuppetFrameInput,
): PuppetXf {
  const puppet = entry && 'views' in entry ? (entry as BundlePuppet) : ((entry as { puppet?: BundlePuppet | null } | null)?.puppet ?? null)
  const cur = resolveAction(puppet, frame.clip)
  if (!puppet || !cur) return EMPTY_XF
  const curView = puppet.views[cur.view] as BundlePuppetView
  const hold = frame.holdWallMs && frame.holdWallMs > 0 ? frame.holdWallMs : 0
  const since = frame.sinceChangeMs + hold
  const prev = frame.prevClip ? resolveAction(puppet, frame.prevClip) : null
  const sameView = prev !== null && prev.view === cur.view
  const w = prev ? crossfadeWeight(since, sameView ? CROSSFADE_MS : VIEW_FADE_MS) : 1
  const k = Number.isFinite(frame.scale) && frame.scale > 0 ? frame.scale : 1
  const kOf = (v: BundlePuppetView): number => (v.height_px > 0 ? (k * curView.height_px) / v.height_px : k)
  const turn = typeof frame.turn === 'number' ? frame.turn : 1
  const idleMs = typeof frame.idleClockMs === 'number' && Number.isFinite(frame.idleClockMs) ? frame.idleClockMs : null
  const timeOf = (b: PuppetBinding, clipMs: number): number =>
    b.action === 'idle' && b.loop && idleMs !== null ? idleMs + hold : clipMs + (b.loop ? hold : 0)

  const curRoot = rootOf(curView)
  const c = sampleAt(curRoot, cur, timeOf(cur, frame.clipTimeMs))
  if (!prev || w >= 1) {
    const pose = withLayer(puppet, cur.view, curRoot, c.pose, frame.layer)
    const v = viewXf(cur.view, curView, pose, k, frame.facing, turn, { alpha: 1, zIndex: 0, anim: cur.anim, frame: c.frame })
    return { binding: cur, prev: null, weight: 1, views: [v] }
  }
  const prevView = puppet.views[prev.view] as BundlePuppetView
  const p = sampleAt(rootOf(prevView), prev, timeOf(prev, frame.prevClipTimeMs))
  if (sameView) {
    const pose = withLayer(puppet, cur.view, curRoot, blendPoses(p.pose, c.pose, w), frame.layer)
    const v = viewXf(cur.view, curView, pose, k, frame.facing, turn, { alpha: 1, zIndex: 0, anim: cur.anim, frame: c.frame })
    return { binding: cur, prev, weight: w, views: [v] }
  }
  const half = VIEW_FADE_MS / 2
  if (since < half) {
    // The old view turns away: full width → edge-on.
    const squeeze = 1 - smoothstep(since / half)
    const old = viewXf(prev.view, prevView, p.pose, kOf(prevView), frame.facing, turn, { alpha: 1, zIndex: 0, anim: prev.anim, frame: p.frame }, squeeze)
    return { binding: cur, prev, weight: w, views: [old] }
  }
  // The new view turns in: edge-on → full width.
  const squeeze = smoothstep((since - half) / half)
  const pose = withLayer(puppet, cur.view, curRoot, c.pose, frame.layer)
  const now = viewXf(cur.view, curView, pose, k, frame.facing, turn, { alpha: 1, zIndex: 0, anim: cur.anim, frame: c.frame }, squeeze)
  return { binding: cur, prev, weight: w, views: [now] }
}

/* --------------------------------------------------------------- sockets */

/** Q4 socket names (`schema/story.ts#SocketName`). */
export type SocketName = 'hand' | 'other_hand' | 'mouth' | 'head' | 'back' | 'top'

/** Name ladders for puppets whose bundle carries no socket table. `top` = bounds top-centre. */
const SOCKET_BONES: Readonly<Record<SocketName, readonly string[]>> = {
  hand: ['hand_r', 'hand_near', 'hand', 'hand_l', 'hand_far', 'paw_front', 'front_paw', 'foot_front_near'],
  // a person's second hand: the other palm (anyone else holds a second thing where the first one is)
  other_hand: ['hand_l', 'hand_far', 'hand', 'hand_r', 'hand_near', 'paw_front', 'front_paw', 'foot_front_near'],
  mouth: ['mouth', 'jaw', 'beak', 'head'],
  head: ['head'],
  back: ['torso', 'body', 'chest', 'spine', 'spine_1', 'spine1', 'pelvis'],
  top: [],
}
const SOCKET_CONTAINS: Readonly<Record<SocketName, readonly string[]>> = {
  hand: ['hand', 'paw'],
  other_hand: ['hand', 'paw'],
  mouth: ['head'],
  head: ['head'],
  back: ['torso', 'body', 'spine'],
  top: [],
}

/** Bone index for a socket in `root` by name: the canonical names first, then any name containing one. */
export function socketBone(root: SkfRoot, name: SocketName): number {
  for (const want of SOCKET_BONES[name] ?? []) {
    const i = root.bones.findIndex((b) => b.name === want)
    if (i >= 0) return i
  }
  for (const part of SOCKET_CONTAINS[name] ?? []) {
    const i = root.bones.findIndex((b) => b.name.toLowerCase().includes(part))
    if (i >= 0) return i
  }
  return -1
}

function isSocketDef(v: unknown): v is PuppetSocket {
  return !!v && typeof v === 'object' && typeof (v as { bone?: unknown }).bone === 'string'
}

/**
 * The bundle's socket for `name` on `view`: `views[view].sockets`, then `puppet.sockets[view]`,
 * then a flat `puppet.sockets[name]` (the three shapes the schema accepts). Null = use the ladder.
 */
export function socketDefFor(puppet: BundlePuppet, view: string, name: SocketName): SocketDef | null {
  const v = puppet.views[view] as (BundlePuppetView & { sockets?: Record<string, PuppetSocket> }) | undefined
  const own = v?.sockets?.[name]
  if (isSocketDef(own)) return own
  const table = puppet.sockets as Record<string, unknown> | undefined
  const perView = table?.[view]
  if (perView && !isSocketDef(perView)) {
    const s = (perView as Record<string, unknown>)[name]
    if (isSocketDef(s)) return s
  }
  const flat = table?.[name]
  return isSocketDef(flat) ? flat : null
}

/** A socket in the view's world (y up): the bundle's bone + offset, the name ladder, or the bounds. */
export function socketWorld(puppet: BundlePuppet, view: string, world: readonly WorldBone[], name: SocketName): { x: number; y: number; angle: number } | null {
  const v = puppet.views[view]
  if (!v) return null
  const root = rootOf(v)
  const def = socketDefFor(puppet, view, name)
  if (def) {
    const p = socketPoint(root, null, world, def)
    if (p) return p
  }
  const ladder: SocketName[] = name === 'hand' ? ['hand', 'mouth'] : name === 'other_hand' ? ['other_hand', 'hand', 'mouth'] : [name]
  for (const n of ladder) {
    const id = socketBone(root, n)
    const b = id >= 0 ? world[id] : undefined
    if (b) return { x: b.pos.x, y: b.pos.y, angle: b.rot }
  }
  const bb = v.bounds
  if (!bb) return null
  // No bone: `top` is the bounds top-centre; anything else sits a little above the middle.
  const y = name === 'top' ? bb.y1 : bb.y0 + (bb.y1 - bb.y0) * (name === 'head' || name === 'mouth' ? 0.85 : 0.55)
  return { x: (bb.x0 + bb.x1) / 2, y, angle: 0 }
}

/**
 * Where a socket is, relative to the feet in stage px (y down), with the view container's flip,
 * turn and squash applied — add it to the actor's feet position to get the stage point. Null when
 * the puppet cannot place it at all.
 */
export function socketAt(puppet: BundlePuppet | null | undefined, xf: PuppetViewXf | undefined, name: SocketName): { x: number; y: number; angle: number } | null {
  if (!puppet || !xf) return null
  const p = socketWorld(puppet, xf.view, xf.world, name)
  if (!p) return null
  const sx = Number.isFinite(xf.scaleX) ? xf.scaleX : xf.mirror ? -1 : 1
  const sy = Number.isFinite(xf.scaleY) ? xf.scaleY : 1
  return { x: p.x * xf.k * sx, y: -(p.y - xf.groundY) * xf.k * sy, angle: sx < 0 ? Math.PI - p.angle : p.angle }
}

/** Rest-pose body height of the view `action` binds to (the §8.2 divisor). */
export function bodyHeightFor(puppet: BundlePuppet | null | undefined, action: string): number {
  const b = resolveAction(puppet, action)
  const h = b ? puppet?.views[b.view]?.height_px : undefined
  return typeof h === 'number' && h > 0 ? h : 0
}

/** Every texture asset id a puppet can draw (all views) — what the loader must have resident. */
export function puppetTextureIds(puppet: BundlePuppet | null | undefined): string[] {
  if (!puppet?.views) return []
  const out = new Set<string>()
  for (const v of Object.values(puppet.views)) for (const id of Object.values(v.textures ?? {})) out.add(id)
  return [...out]
}
